import { useEffect, useMemo, useState } from 'react';
import type { CreateTaskInput, TaskRecord, TaskStatus, UpdateTaskInput } from '@shared/types';
import { Icon } from '@renderer/shared/components/icons';
import { SelectField, TextAreaField, TextField } from '@renderer/shared/components/primitives';

type Draft = {
  title: string;
  description: string;
  status: TaskStatus;
  projectId: string;
  dueDate: string;
};

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string {
  return `${value}T12:00:00.000Z`;
}

type TaskEditModalProps = {
  isOpen: boolean;
  task: TaskRecord;
  statusOptions: Array<{ value: TaskStatus; label: string }>;
  projectOptions: Array<{ value: string; label: string }>;
  strings: {
    close: string;
    title: string;
    save: string;
    cancel: string;
    fieldTitle: string;
    fieldDescription: string;
    fieldStatus: string;
    fieldProject: string;
    fieldDueAt: string;
    validationTitleRequired: string;
  };
  onClose: () => void;
} & (
  | {
      mode?: 'edit';
      onSave: (payload: UpdateTaskInput) => Promise<void>;
    }
  | {
      mode: 'create';
      onSave: (payload: CreateTaskInput) => Promise<void>;
    }
);

export function TaskEditModal({
  isOpen,
  task,
  statusOptions,
  projectOptions,
  strings,
  onClose,
  onSave,
  mode = 'edit'
}: TaskEditModalProps): JSX.Element | null {
  const initialDraft = useMemo<Draft>(
    () => ({
      title: task.title,
      description: task.description ?? '',
      status: task.status,
      projectId: task.projectId ?? '',
      dueDate: formatDateInputValue(task.dueAt)
    }),
    [task.description, task.dueAt, task.projectId, task.status, task.title]
  );

  const [draft, setDraft] = useState<Draft>(initialDraft);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDraft(initialDraft);
    setError(null);
  }, [initialDraft, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const isDirty =
    draft.title !== task.title ||
    draft.description !== (task.description ?? '') ||
    draft.status !== task.status ||
    draft.projectId !== (task.projectId ?? '') ||
    draft.dueDate !== formatDateInputValue(task.dueAt);

  async function handleSave(): Promise<void> {
    if (!isDirty) {
      onClose();
      return;
    }

    if (!draft.title.trim()) {
      setError(strings.validationTitleRequired);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        title: draft.title.trim(),
        description: draft.description.trim() ? draft.description.trim() : null,
        status: draft.status,
        projectId: draft.projectId ? draft.projectId : null,
        dueAt: draft.dueDate ? toIsoDate(draft.dueDate) : null
      };

      if (mode === 'create') {
        const saveCreate = onSave as (nextPayload: CreateTaskInput) => Promise<void>;
        await saveCreate({
          ...payload,
          customerId: task.customerId,
          priority: task.priority,
          taskLabelIds: task.taskLabelIds
        });
      } else {
        const saveUpdate = onSave as (nextPayload: UpdateTaskInput) => Promise<void>;
        await saveUpdate({
          id: task.id,
          ...payload
        });
      }
      onClose();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className="av-task-modal__overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div aria-modal="true" className="av-task-modal av-task-modal--edit" role="dialog" onClick={(event) => event.stopPropagation()}>
        <div className="av-task-modal__header">
          <div className="av-task-modal__title">
            <h2>{strings.title}</h2>
          </div>
          <button aria-label={strings.close} className="av-task-modal__close" type="button" onClick={onClose}>
            <Icon name="times" />
          </button>
        </div>

        <div className="av-task-modal__body">
          {error ? <div className="av-inline-error">{error}</div> : null}

          <label className="av-form-field">
            <span>{strings.fieldTitle}</span>
            <TextField value={draft.title} onValueChange={(value) => setDraft((current) => ({ ...current, title: value }))} />
          </label>

          <label className="av-form-field">
            <span>{strings.fieldDescription}</span>
            <TextAreaField
              className="av-textarea"
              value={draft.description}
              onValueChange={(value) => setDraft((current) => ({ ...current, description: value }))}
              rows={4}
            />
          </label>

          <div className="av-form-grid av-form-grid--two">
            <label className="av-form-field">
              <span>{strings.fieldStatus}</span>
              <SelectField value={draft.status} options={statusOptions} onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value as TaskStatus }))} />
            </label>
            <label className="av-form-field">
              <span>{strings.fieldProject}</span>
              <SelectField value={draft.projectId} options={projectOptions} onChange={(event) => setDraft((current) => ({ ...current, projectId: event.target.value }))} />
            </label>
          </div>

          <label className="av-form-field">
            <span>{strings.fieldDueAt}</span>
            <TextField
              type="date"
              value={draft.dueDate}
              onValueChange={(value) => setDraft((current) => ({ ...current, dueDate: value }))}
            />
          </label>
        </div>

        <div className="av-task-modal__footer">
          <button className="av-secondary-button" type="button" onClick={onClose} disabled={isSaving}>
            {strings.cancel}
          </button>
          <button className="av-primary-button" type="button" onClick={() => void handleSave()} disabled={isSaving}>
            {strings.save}
          </button>
        </div>
      </div>
    </div>
  );
}
